import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import withRouter from "components/Common/withRouter";
import { useNavigate } from "react-router-dom";
import { DEFAULT_PAGE_SIZE, mapApiMetadataToTableColumnDefinition, urlEndPoint } from "helpers/GeneralHelper";
import { toast } from "react-toastify";
import dayjs from "dayjs";

import { Card, CardBody, Container } from "reactstrap";
import EcommerceOrdersModal from "components/Common/EcommerceOrdersModal";
import TableContainer from "components/Common/TableContainer";
import MetadataFilterToolbar from "components/Common/MetadataFilterToolbar";
import AllCommonFilterModal from "pages/AllCommonFilter/all-common-filter";
import ClientRegisterAddEditModal from "pages/AllCommonModal/ClientRegisterAddEditModal.jsx/client-register-add-edit-modal";
import { useClientSectionContext } from "libs/context/ClientSectionContext";
import { usePermissionContext } from "libs/context/PermissionContext";
import {
    submitClientSectionFilterData,
    updateClientSectionStatus,
} from "model/ClientSectionModel";
import Loaders from "components/Common/Loaders";
import Swal from "sweetalert2";
import {
    getAsyncFilterLabelFieldKey,
    isAsyncFilterLabelFieldKey,
    isMetadataAsyncSelectFilterDefinition,
    mergeModalSubmitIntoListFilter,
    normalizeMetadataFilterDateForApi,
} from "helpers/metadataFilterHelpers";
import { useMetadataDrivenFilters } from "hooks/useMetadataDrivenFilters";
import { useCommonTableSettingsPageSize } from "hooks/useSettingsReadyTablePageSize";

const CLIENT_PAGE_NAME = "Client";

const strOrNull = (v) => {
    if (v === null || v === undefined) return null;
    const s = String(v).trim();
    return s === "" ? null : s;
};

const normalizeClientFilterPayload = (raw = {}, metadataFilters = []) => {
    const out = { ...raw };
    const dateFields = new Set(
        (Array.isArray(metadataFilters) ? metadataFilters : [])
            .filter((f) => f?.type === "date" && f?.field)
            .map((f) => f.field)
    );

    (Array.isArray(metadataFilters) ? metadataFilters : []).forEach((def) => {
        const field = def?.field;
        if (!field || !(field in out)) return;
        if (def?.type === "date" || dateFields.has(field)) {
            out[field] = normalizeMetadataFilterDateForApi(out[field]);
        } else if (typeof out[field] === "string") {
            out[field] = strOrNull(out[field]);
        }
    });

    ["nameContains", "statusId", "fromDate", "toDate"].forEach((key) => {
        if (!(key in out)) return;
        if (dateFields.has(key) || key === "fromDate" || key === "toDate") {
            out[key] = normalizeMetadataFilterDateForApi(out[key]);
        } else if (typeof out[key] === "string") {
            out[key] = strOrNull(out[key]);
        }
    });

    Object.keys(out).forEach((key) => {
        if (isAsyncFilterLabelFieldKey(key)) delete out[key];
    });
    delete out.search;
    return out;
};

const Client = () => {
    const navigate = useNavigate();
    const { permissionData } = usePermissionContext() || {};
    const { fetchClientSectionContextData, clientSectionContextData } = useClientSectionContext() || {};
    const { pageSize: settingsPageSize, settingsReady } = useCommonTableSettingsPageSize(CLIENT_PAGE_NAME);
    const initialFilterFetchedRef = useRef(false);
    const [clientAddEditModalOpen, setClientAddEditModalOpen] = useState(false);
    const [modal1, setModal1] = useState(false);
    /** Filled from Client metadata filters (`nameContains`, `statusId`, `fromDate`, `toDate`). */
    const [filterForm, setFilterForm] = useState({});
    const [clientData, setClientData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [itemId, setItemId] = useState(null);
    const [tableMetaData, setTableMetadata] = useState();
    const [commonFilterModalOpen, setCommonFilterModalOpen] = useState(false);
    const [commonResetFilter, setCommonResetFilter] = useState(false);
    const [requestPayload, setRequestPayload] = useState({ pageNumber: 1, pageSize: DEFAULT_PAGE_SIZE });
    const [paginationMeta, setPaginationMeta] = useState({
        pageNumber: 1,
        pageSize: DEFAULT_PAGE_SIZE,
        totalPages: 0,
        totalRecords: 0,
    });
    const toggleViewModal = () => setModal1(!modal1);

    const filteredData = useMemo(() => clientData, [clientData]);

    const clientActionPermissions = useMemo(
        () => ({
            canEdit: Boolean(permissionData?.ClientUpdateAccess),
            canShowCompanyUser: Boolean(permissionData?.ClientCompanyUserAccess),
        }),
        [permissionData]
    );

    const hasAnyClientRowAction = useMemo(
        () =>
            clientActionPermissions.canEdit || clientActionPermissions.canShowCompanyUser,
        [clientActionPermissions]
    );

    const {
        shouldShowTableHeaderFilterAndResetControls,
        shouldRenderMetadataToolbarFilterRow,
        activeModalFilterCount,
        modalFilterData: clientFilterModalData,
        metadataToolbarFilterDefinitions,
        metadataFiltersAll,
        usesMetadataDrivenFilterUi,
        buildBaselineEmptyListFilter,
        toolbarDropdownReactSelectOptionsByFieldName,
    } = useMetadataDrivenFilters(
        clientSectionContextData?.filters,
        filterForm,
        CLIENT_PAGE_NAME
    );

    /** API `status`: true → active, false → deactive (for table + toggle). */
    const mapClientStatusFromApi = (value) => {
        if (value === true || value === "true" || value === 1 || value === "1") {
            return "active";
        }
        if (value === false || value === "false" || value === 0 || value === "0") {
            return "deactive";
        }
        const raw = String(value ?? "").trim().toLowerCase();
        if (raw === "active") return "active";
        if (raw === "deactive" || raw === "inactive") return "deactive";
        return "deactive";
    };

    const normalizeClientRowStatus = (row = {}) =>
        mapClientStatusFromApi(row?.statusRaw ?? row?.status);

    const isSuccessResponse = (res) =>
        res?.success !== false &&
        (res?.success || res?.isSuccess || res?.data != null);

    const formatDateToMMDDYYYY = (value) => {
        if (!value) return "";
        const parsedDate = dayjs(value);
        if (!parsedDate.isValid()) return value;
        return parsedDate.format("MM-DD-YYYY");
    };

    useEffect(() => {
        const metadataFilters = clientSectionContextData?.filters;
        if (!Array.isArray(metadataFilters) || !metadataFilters.length) return;
        setFilterForm((previousFilterState) => {
            const mergedFilterState = { ...previousFilterState };
            let mergedNewKeysIntoState = false;
            metadataFilters.forEach((filterDefinitionRow) => {
                const fieldKey = filterDefinitionRow?.field;
                if (!fieldKey) return;
                if (!(fieldKey in mergedFilterState)) {
                    mergedFilterState[fieldKey] =
                        filterDefinitionRow?.type === "date" ? null : "";
                    mergedNewKeysIntoState = true;
                }
                if (isMetadataAsyncSelectFilterDefinition(filterDefinitionRow)) {
                    const asyncLabelFieldKey = getAsyncFilterLabelFieldKey(fieldKey);
                    if (!(asyncLabelFieldKey in mergedFilterState)) {
                        mergedFilterState[asyncLabelFieldKey] = "";
                        mergedNewKeysIntoState = true;
                    }
                }
            });
            return mergedNewKeysIntoState ? mergedFilterState : previousFilterState;
        });
    }, [clientSectionContextData?.filters]);

    useEffect(() => {
        fetchClientSectionContextData?.();
    }, []);

    useEffect(() => {
        if (!settingsReady || initialFilterFetchedRef.current) return;
        initialFilterFetchedRef.current = true;
        const pageSize = Number(settingsPageSize) || DEFAULT_PAGE_SIZE;
        setRequestPayload((previous) => ({ ...previous, pageSize }));
        setPaginationMeta((previous) => ({ ...previous, pageSize }));
        getClientList({ pageNumber: 1, pageSize });
    }, [settingsReady, settingsPageSize]);

    useEffect(() => {
        const valueReModified = clientSectionContextData?.table?.columns?.map((col) => ({
            ...mapApiMetadataToTableColumnDefinition(col),
        }));
        setTableMetadata(valueReModified);
    }, [clientSectionContextData]);

    const buildPayloadFromFilterForm = useCallback(
        (form = {}, overrides = {}) => {
            const metadataFilters = clientSectionContextData?.filters || [];
            const looselyMapped = {};
            metadataFilters.forEach((def) => {
                const field = def?.field;
                if (!field) return;
                looselyMapped[field] = form?.[field];
            });

            return normalizeClientFilterPayload(
                {
                    pageNumber: 1,
                    pageSize: requestPayload.pageSize || DEFAULT_PAGE_SIZE,
                    ...looselyMapped,
                    nameContains: strOrNull(
                        form?.nameContains ?? form?.search
                    ),
                    fromDate: form?.fromDate || null,
                    toDate: form?.toDate || null,
                    statusId: strOrNull(form?.statusId) || null,
                    ...overrides,
                },
                metadataFilters
            );
        },
        [clientSectionContextData?.filters, requestPayload.pageSize]
    );

    const getClientList = async (payloadOverrides = {}) => {
        setLoading(true);
        const payload = normalizeClientFilterPayload(
            {
                ...requestPayload,
                ...payloadOverrides,
            },
            clientSectionContextData?.filters || []
        );
        const response = await submitClientSectionFilterData(payload);
        const responseData = response?.data || {};
        const listData = responseData?.records || [];
        const valueReModified = listData?.map((item = {}) => ({
            ...item,
            id: item?.id || item?._id || "",
            company: item?.company || item?.companyName || "",
            companyId:
                item?.companyId ||
                item?.clientCompanyId ||
                item?.company_id ||
                "",
            client: item?.client || item?.clientName || item?.name || "",
            contact: item?.contact || item?.contactName || "",
            email: item?.email || "",
            phone: item?.phone || item?.phoneNumber || "",
            subscribed: item?.subscribed || item?.isSubscribed || "",
            billDate: item?.billDate || item?.bill_date || "",
            approved: item?.approved || item?.approvedDate || "",
            registerDate: formatDateToMMDDYYYY(item?.registerDate),
            statusRaw: item?.status,
            status: mapClientStatusFromApi(item?.status),
        }));

        if (response) {
            setClientData(valueReModified || []);
            setPaginationMeta({
                pageNumber: Number(responseData?.pageNumber) || Number(payload?.pageNumber) || 1,
                pageSize: Number(responseData?.pageSize) || Number(payload?.pageSize) || DEFAULT_PAGE_SIZE,
                totalPages: Number(responseData?.totalPages) || 0,
                totalRecords: Number(responseData?.totalRecords) || 0,
            });
            setRequestPayload(payload);
        } else {
            setClientData([]);
        }
        setLoading(false);
    };

    const handleViewCompanyUsers = (row) => {
        const companyId = String(
            row?.companyId || row?.clientCompanyId || row?.company_id || ""
        ).trim();
        if (!companyId) {
            toast.error("Company id is missing");
            return;
        }
        navigate(`/${urlEndPoint.companyUser}/${encodeURIComponent(companyId)}`);
    };

    const handleStatusEdit = async (row) => {
        const id = String(row?.id || row?._id || "").trim();
        if (!id) {
            toast.error("Client id is missing");
            return;
        }

        const currentStatus = normalizeClientRowStatus(row);
        const nextStatus = currentStatus === "active" ? "deactive" : "active";

        const swalResult = await Swal.fire({
            title: "Update client status?",
            html: `Change status from <strong>${currentStatus}</strong> to <strong>${nextStatus}</strong>?`,
            icon: "question",
            showCancelButton: true,
            confirmButtonText: "Submit",
            cancelButtonText: "Cancel",
            reverseButtons: true,
        });
        if (!swalResult?.isConfirmed) return;

        setLoading(true);
        try {
            const response = await updateClientSectionStatus(id, { status: nextStatus });
            if (!isSuccessResponse(response)) {
                toast.error(response?.message || "Unable to update status");
                return;
            }
            toast.success(response?.message || "Status updated successfully");
            await getClientList({ pageNumber: paginationMeta?.pageNumber || 1 });
        } finally {
            setLoading(false);
        }
    };

    const handleFilterSubmit = async (modalFormPayload = {}) => {
        const mergedFilterForm = mergeModalSubmitIntoListFilter(
            filterForm,
            clientFilterModalData,
            modalFormPayload
        );
        setFilterForm(mergedFilterForm);
        setCommonFilterModalOpen(false);
        await getClientList(buildPayloadFromFilterForm(mergedFilterForm));
    };

    const handleFilterReset = useCallback(async () => {
        const emptyFilter = buildBaselineEmptyListFilter();
        setFilterForm(emptyFilter);
        setCommonFilterModalOpen(false);
        await getClientList({
            ...buildPayloadFromFilterForm(emptyFilter),
            pageNumber: 1,
            pageSize: paginationMeta.pageSize || DEFAULT_PAGE_SIZE,
        });
    }, [buildBaselineEmptyListFilter, buildPayloadFromFilterForm, paginationMeta.pageSize]);

    useEffect(() => {
        if (!commonResetFilter) return;
        const run = async () => {
            await handleFilterReset();
            setCommonResetFilter(false);
        };
        run();
    }, [commonResetFilter, handleFilterReset]);

    const handleToolbarTextFilterInputChange = (fieldKey, nextValue) => {
        setFilterForm((previousFilterState) => ({
            ...previousFilterState,
            [fieldKey]: nextValue ?? "",
        }));
    };

    const handleToolbarReactSelectFilterChange = (fieldKey, selectedOption) => {
        setFilterForm((previousFilterState) => ({
            ...previousFilterState,
            [fieldKey]: selectedOption?.value ?? "",
            [getAsyncFilterLabelFieldKey(fieldKey)]: selectedOption?.label ?? "",
        }));
    };

    const handleUpperSearch = () => {
        getClientList(buildPayloadFromFilterForm(filterForm));
    };

    const handlePageChange = async (page) => {
        await getClientList({ pageNumber: page });
    };

    const handleRowsPerPageChange = async (rows) => {
        await getClientList({ pageNumber: 1, pageSize: rows });
    };

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <EcommerceOrdersModal isOpen={modal1} toggle={toggleViewModal} />

                    <Card>
                        <CardBody>
                            <h5 className="fw-bold mb-3">Client</h5>
                            <hr />

                            {shouldRenderMetadataToolbarFilterRow ? (
                                <MetadataFilterToolbar
                                    toolbarDefinitions={metadataToolbarFilterDefinitions}
                                    listFilter={filterForm}
                                    onTextChange={handleToolbarTextFilterInputChange}
                                    onSelectChange={handleToolbarReactSelectFilterChange}
                                    onSearch={handleUpperSearch}
                                    onClear={handleFilterReset}
                                    showClearWhenNoModalFilters={false}
                                    toolbarDropdownOptionsByFieldName={
                                        toolbarDropdownReactSelectOptionsByFieldName
                                    }
                                    pageName={CLIENT_PAGE_NAME}
                                />
                            ) : null}

                            {
                            !loading && tableMetaData?.length ? (
                                <TableContainer
                                    columns={tableMetaData}
                                    data={filteredData}
                                    isActionRequired={hasAnyClientRowAction}
                                    onEdit={
                                        clientActionPermissions.canEdit
                                            ? handleStatusEdit
                                            : undefined
                                    }
                                    // onView={
                                    //     clientActionPermissions.canShowCompanyUser
                                    //         ? handleViewCompanyUsers
                                    //         : undefined
                                    // }
                                    isCheckboxLeftSide={false}
                                    isPagination={true}
                                    currentPage={paginationMeta?.pageNumber}
                                    rowsPerPage={paginationMeta?.pageSize}
                                    totalPages={paginationMeta?.totalPages}
                                    totalRecords={paginationMeta?.totalRecords}
                                    onPageChange={handlePageChange}
                                    onPageSizeChange={handleRowsPerPageChange}
                                    setCommonFilterModalOpen={setCommonFilterModalOpen}
                                    setCommonResetFilter={setCommonResetFilter}
                                    allCommonFilter={
                                        shouldShowTableHeaderFilterAndResetControls &&
                                        clientFilterModalData.length > 0
                                    }
                                    activeFilterCount={activeModalFilterCount}
                                    pageName={CLIENT_PAGE_NAME}
                                />
                            ) : (
                                <Loaders />
                            )}
                        </CardBody>
                    </Card>

                    {clientFilterModalData.length > 0 ? (
                        <AllCommonFilterModal
                            setCommonFilterModalOpen={setCommonFilterModalOpen}
                            commonFilterModalOpen={commonFilterModalOpen}
                            modalTitle="Client"
                            handleSubmit={handleFilterSubmit}
                            filterData={clientFilterModalData}
                            metadataFilterDefinitions={metadataFiltersAll}
                            enableFilterToolbarCustomization={usesMetadataDrivenFilterUi}
                            commonResetFilter={commonResetFilter}
                            setCommonResetFilter={setCommonResetFilter}
                            pageName={CLIENT_PAGE_NAME}
                        />
                    ) : null}

                    <ClientRegisterAddEditModal
                        setCompanyAddEditModalOpen={setClientAddEditModalOpen}
                        companyAddEditModalOpen={clientAddEditModalOpen}
                        getCompanyList={getClientList}
                        itemId={itemId}
                    />
                </Container>
            </div>
        </React.Fragment>
    );
};

Client.propTypes = {
    orders: PropTypes.array,
    onGetOrders: PropTypes.func,
};

export default withRouter(Client);
